import { useUserSession } from '@solinkcorp/authentication';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import useScheduledActivities from 'components/ScheduledActivityForm/useScheduledActivities';
import React from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import FeaturesUtil from 'utils/FeaturesUtil';
import { isScheduledActivityOngoing } from 'utils/RecurrenceUtil';
import { SCHEDULED_AI_CHAT_REASON } from 'vd-constants';

export function useSpotChecks() {
  const scheduledActivities = useScheduledActivities();
  const camerasById = useFluxStore(LocationStore, (store) => store.getAllLocationCamerasDict());
  const user = useUserSession();
  const hasAccessToAllLocations = FeaturesUtil.userHasAccessToAllLocations(user);

  const data = React.useMemo(() => {
    return scheduledActivities.data?.filter((activity) => {
      // Since spot checks are not tied to a specific device, we need to check if the user has access to all of the cameras
      // We can hopefully remove this check once spot checks become tied to multiple devices
      // There's some scenarios where devices get deactivated and the cameraIds are still tied to the spot check
      // The hasAccessToAllLocations check will allow users to see spot checks for devices that are no longer active to update them
      const doesUserHavePermissionToSeeSpotCheck =
        hasAccessToAllLocations || activity.cameraIds.every((cameraId) => camerasById[cameraId]);
      return activity.reason === SCHEDULED_AI_CHAT_REASON && doesUserHavePermissionToSeeSpotCheck;
    });
  }, [hasAccessToAllLocations, scheduledActivities.data, camerasById]);

  return { ...scheduledActivities, data };
}

export type CategorizedSpotChecks = Record<string, ScheduledActivityEntity[]>;

export function useCategorizedSpotChecks() {
  const spotChecks = useSpotChecks();

  return React.useMemo(() => {
    return spotChecks.data?.reduce(
      (acc, spotCheck) => {
        const isActive = isScheduledActivityOngoing(spotCheck);

        if (spotCheck?.metadata?.isTemplate) {
          acc.templates.push(spotCheck);
        } else if (isActive) {
          acc.active.push(spotCheck);
        } else {
          acc.archived.push(spotCheck);
        }

        return acc;
      },
      {
        active: [],
        archived: [],
        templates: [],
      } as CategorizedSpotChecks,
    );
  }, [spotChecks.data]);
}
